import React, { useCallback } from 'react'

import {
  routes,
  getTriggerCopyFromAccessLevel,
  WorkspaceRow,
} from '@sketch/modules-common'

// Components
import { ProjectAccessSelect } from 'modules/projects/components/ProjectAccessSelect/ProjectAccessSelect'
import {
  Breakpoint,
  ConfirmationDialog,
  Flex,
  Button,
  Modal,
  InfiniteList,
  handleFetchMore,
  useModalContext,
  ModalInjectedProps,
} from '@sketch/components'
import { WorkspaceMemberRow } from 'modules/projects/components/MemberRow'

import {
  RemoveAccessRestrictionsFromProjectModal,
  RestrictAccessToProjectModal,
} from './ProjectSharingModal.modals'
import { FormValues } from '../AddNewProjectModal/useAutocomplete'

import { AddMemberForm } from './ProjectSharingForm'
import { EditProjectMemberPermissionsAccessSelect } from 'modules/projects/components/ProjectMemberPermissionsAccessSelect/ProjectMemberPermissionsAccessSelect'

// Styles
import { CopyButton } from 'modules/shares/modals/ShareSettingsModal/PublicPermissions/PublicPermissions.styles'
import {
  StyledModal,
  StyledSeparator,
  StyledBreadcrumbs,
  PermissionText,
  WorkspaceRowWarningText,
  ExclamationMark,
} from './ProjectSharingModal.styles'

// Helpers
import { dataIdFromObject } from '@sketch/graphql-cache'
import { addProjectMembership } from '../../operations/utils/projectMemberships.cache'
import { useProjectMembershipChanged } from 'modules/projects/operations/useProjectMembershipChanged/useProjectMembershipChanged'
import { useUpdateProjectMember } from 'modules/projects/hooks/useUpdateProjectMember/useUpdateProjectMember'

import {
  ProjectFragment,
  WorkspaceMinimalFragment,
  useGetProjectMembershipsQuery,
  useAddProjectMemberMutation,
  useUpdateProjectPermissionMutation,
  useGetWorkspaceQuery,
  ProjectMembershipFragment,
} from '@sketch/gql-types'

interface ProjectSharingContentProps {
  project: ProjectFragment
  workspace: WorkspaceMinimalFragment
  editable?: boolean
}

interface ProjectSharingModalProps extends ModalInjectedProps {
  project: ProjectFragment
  workspace?: WorkspaceMinimalFragment
  workspaceId: string
  editable?: boolean
}

interface ProjectMemberRowProps {
  member: ProjectMembershipFragment
  project: ProjectFragment
  workspaceId: string
  editable?: boolean
}

export const ProjectSharingModal: React.FC<ProjectSharingModalProps> = ({
  project,
  workspace,
  workspaceId,
  hideModal,
  editable = true,
}) => {
  const { data } = useGetWorkspaceQuery({
    fetchPolicy: 'cache-only',
    variables: { identifier: workspaceId },
    skip: Boolean(workspace),
  })

  const workspaceData = workspace || data?.workspace

  const projectRoute = routes.WORKSPACE_PROJECT.create({
    projectId: project.identifier,
    workspaceId,
  })

  return (
    <StyledModal onCancel={hideModal}>
      <Modal.Header as="div">
        <Flex justifyContent="space-between" alignItems="center">
          <header>
            Share Project
            <StyledBreadcrumbs workspace={workspaceData} project={project} />
          </header>
          <Breakpoint on="sm">
            <CopyButton
              size="40"
              showIcon={false}
              value={`${window.location.origin}${projectRoute}`}
              title="Copy Link"
            />
          </Breakpoint>
        </Flex>
        <StyledSeparator />
      </Modal.Header>

      <Modal.Body>
        <ProjectSharingModalContent
          project={project}
          workspace={workspaceData!}
          editable={editable}
        />
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={hideModal}>
          Done
        </Button>
      </Modal.Footer>
    </StyledModal>
  )
}

const ProjectSharingModalContent = (props: ProjectSharingContentProps) => {
  const { project, workspace, editable } = props
  const { showModal } = useModalContext()

  const { error, data, fetchMore } = useGetProjectMembershipsQuery({
    variables: { projectIdentifier: project.identifier },
    fetchPolicy: 'cache-and-network',
  })

  useProjectMembershipChanged()

  const [updateProjectPermission] = useUpdateProjectPermissionMutation({
    onError: 'show-toast',
    update(_, response) {
      showModal(ProjectSharingModal, {
        project: response.data?.updateProjectPermission
          .project as ProjectFragment,
        workspaceId: workspace.identifier,
      })
    },
  })

  const [addMemberToProject] = useAddProjectMemberMutation({
    onError: 'show-toast',
    update(cache, response) {
      if (!response.data?.addProjectMember.projectMembership) {
        return
      }

      addProjectMembership({
        cache,
        projectIdentifier: project.identifier,
        membership: response.data?.addProjectMember.projectMembership,
      })
    },
  })

  const members = data?.project.memberships.entries || []
  const after = data?.project.memberships.meta.after

  const membersList = members.map(member => member.workspaceMembership)

  const handleAddMember = useCallback(
    async (values: FormValues) => {
      const variables = {
        projectIdentifier: project.identifier,
        email: values.email || undefined,
        workspaceMembershipIdentifier:
          values.workspaceMembershipIdentifier || undefined,
        guestCommentsEnabled: values.guestCommentsEnabled,
        guestInspectEnabled: values.guestInspectEnabled,
      }

      await addMemberToProject({ variables })
    },
    [addMemberToProject, project.identifier]
  )

  const handleChangeWorkspaceAccessLevel = useCallback(
    (newWorkspaceAccessLevel: 'DEFAULT' | 'NONE') => {
      const onCancelAction = () => {
        showModal(ProjectSharingModal, {
          project,
          workspaceId: workspace.identifier,
        })
      }

      const onConfirm = async () => {
        await updateProjectPermission({
          variables: {
            projectIdentifier: project.identifier,
            workspaceAccessLevel: newWorkspaceAccessLevel,
          },
        })

        showModal(ProjectSharingModal, {
          project: {
            ...project,
            workspaceAccessLevel: newWorkspaceAccessLevel,
          },
          workspaceId: workspace.identifier,
        })
      }

      const modalProps =
        newWorkspaceAccessLevel === 'NONE'
          ? RestrictAccessToProjectModal({
              project,
              onConfirm,
              onCancelAction,
            })
          : RemoveAccessRestrictionsFromProjectModal({
              project,
              onConfirm,
              onCancelAction,
            })

      showModal(ConfirmationDialog, modalProps)
    },
    [project, showModal, updateProjectPermission, workspace.identifier]
  )

  if (error) {
    return null
  }

  return (
    <>
      {editable && (
        <AddMemberForm
          workspace={workspace}
          projectIdentifier={project.identifier}
          membersList={membersList}
          onMemberAdd={handleAddMember}
        />
      )}

      <InfiniteList
        canLoadMore={after !== null}
        onLoadMore={handleFetchMore(
          fetchMore,
          ['project', 'memberships', 'entries'],
          {
            dataIdFromObject,
            after,
          }
        )}
      >
        <WorkspaceRow
          workspace={workspace}
          actions={
            editable ? (
              <ProjectAccessSelect
                level={project.workspaceAccessLevel}
                setLevel={handleChangeWorkspaceAccessLevel}
                hideCommentLabel
              />
            ) : (
              <PermissionText
                noAccess={project.workspaceAccessLevel === 'NONE'}
              >
                {getTriggerCopyFromAccessLevel(project.workspaceAccessLevel)}
              </PermissionText>
            )
          }
          warningText={
            members.length === 0 && (
              <WorkspaceRowWarningText>
                <ExclamationMark />
                Admins always have access to restricted projects
              </WorkspaceRowWarningText>
            )
          }
        />
        {members.map(member => (
          <ProjectMemberRow
            key={member.identifier}
            member={member}
            project={project}
            workspaceId={workspace.identifier}
            editable={editable}
          />
        ))}
      </InfiniteList>
    </>
  )
}

const ProjectMemberRow = ({
  member,
  project,
  workspaceId,
  editable,
}: ProjectMemberRowProps) => {
  const { showModal } = useModalContext()
  const { accessLevelProps } = useUpdateProjectMember({ member })

  return (
    <WorkspaceMemberRow
      workspaceMembership={member.workspaceMembership}
      inviteStatus={member.inviteStatus}
      actions={
        editable ? (
          <EditProjectMemberPermissionsAccessSelect
            project={project}
            member={member}
            {...accessLevelProps}
            onAction={() => {
              showModal(ProjectSharingModal, { project, workspaceId })
            }}
          />
        ) : (
          <PermissionText>
            {getTriggerCopyFromAccessLevel(
              member.privateAccessLevel === 'EDIT' ? 'EDIT' : 'VIEW'
            )}
          </PermissionText>
        )
      }
    />
  )
}
